import { useState } from "react";
import { CommonButton, CommonInput, CommonSelect } from "../Input";
import DataTable from "../table/DataTable";
import CreateSingleTransactionModal from "./CreateSingleTransactionModal";
import SendBulkTransactionModal from "./SendBulkTransactionModal";
import { initialData } from "./mockData";
import TransactionInformationModal from "./TransactionInformationModal";

const TransactionsList = () => {
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateSingleModal, setShowCreateSingleModal] = useState(false);
  const [showCreateBulkModal, setShowCreateBulkModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [filters, setFilters] = useState({
    transactionId: "",
    paymentMethod: null,
    status: null,
  });

  // Data is now imported from mockData.js

  const [displayData, setDisplayData] = useState(initialData);

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleSearch = () => {
    let filtered = initialData.filter((item) => {
      const matchTransaction =
        !filters.transactionId ||
        item.id.toLowerCase().includes(filters.transactionId.toLowerCase());
      const matchPayment =
        !filters.paymentMethod ||
        item.paymentMethod.toLowerCase() ===
          filters.paymentMethod.label.toLowerCase();
      const matchStatus =
        !filters.status || item.statusValue === filters.status.value;
      return matchTransaction && matchPayment && matchStatus;
    });
    setDisplayData(filtered);
  };

  const handleReset = () => {
    setFilters({
      transactionId: "",
      paymentMethod: null,
      status: null,
    });
    setDisplayData(initialData);
  };

  const columns = [
    {
      label: "ID",
      key: "id",
      className: "ps-4",
      cellClassName: "align-middle",
      style: { minWidth: "120px" },
      render: (item) => (
        <span className="fw-bold text-black fs-13">{item.id}</span>
      ),
    },
    {
      label: "Date & Time",
      key: "createdDate",
      cellClassName: "align-middle",
      style: { minWidth: "120px" },
      render: (item) => (
        <div className="fs-12">
          <div className="fw-medium text-black">{item.displayDate}</div>
          <div className="text-muted">{item.time}</div>
        </div>
      ),
    },
    {
      label: "Sender Name",
      key: "senderName",
      cellClassName: "align-middle",
      style: { minWidth: "200px" },
      render: (item) => (
        <div className="fs-12">
          <div className="fw-bold text-black">{item.senderName}</div>
          <div className="text-muted">{item.senderEmail}</div>
        </div>
      ),
    },
    {
      label: "Receiver Name",
      key: "receiverName",
      cellClassName: "align-middle",
      style: { minWidth: "200px" },
      render: (item) => (
        <span className="fw-bold text-black fs-13">{item.receiverName}</span>
      ),
    },
    {
      label: "Payment Method",
      key: "paymentMethod",
      cellClassName: "align-middle",
      style: { minWidth: "140px" },
      render: (item) => (
        <span className="fw-medium text-black fs-13">{item.paymentMethod}</span>
      ),
    },
    {
      label: "Send Amount",
      key: "sendAmount",
      cellClassName: "align-middle",
      style: { minWidth: "120px" },
      render: (item) => (
        <div className="fs-12">
          <div className="fw-bold text-black fs-13">
            {item.sendAmount} {item.currency}
          </div>
          {item.itemCount && <div className="text-muted">{item.itemCount}</div>}
        </div>
      ),
    },
    {
      label: "Status",
      key: "status",
      cellClassName: "align-middle",
      style: { minWidth: "200px" },
      render: (item) => {
        let color = "#fff";
        let bgColor = "#4D8DFB";
        let borderColor = "#4D8DFB";

        if (item.statusValue === "waiting_admin") {
          bgColor = "#F59D56";
          borderColor = "#F59D56";
        } else if (item.statusValue === "waiting_aml") {
          color = "#6B72F7";
          bgColor = "#fff";
          borderColor = "#6B72F7";
        }

        return (
          <span
            className="px-2 py-1 border rounded d-inline-block"
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: color,
              backgroundColor: bgColor,
              borderColor: borderColor,
            }}
          >
            {item.status}
          </span>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      className: "pe-4 text-center",
      cellClassName: "align-middle text-center",
      style: { minWidth: "80px" },
      render: (item) => (
        <button
          className="btn btn-xs p-0 border-0 bg-transparent text-muted"
          onClick={() => {
            setSelectedTransaction(item);
            setShowViewModal(true);
          }}
        >
          <i className="far fa-eye fs-14"></i>
        </button>
      ),
    },
  ];

  const paymentMethodOptions = [
    { label: "Cash", value: "cash" },
    { label: "Bank Transfer", value: "bank_transfer" },
  ];

  const statusOptions = [
    { label: "Waiting admin approval", value: "waiting_admin" },
    { label: "Waiting for AML transaction approval", value: "waiting_aml" },
    { label: "Processing", value: "processing" },
    { label: "Done", value: "done" },
    { label: "Rejected", value: "rejected" },
  ];

  const userRole = localStorage.getItem("userRole");
  const isSupervisor = userRole?.toLowerCase() === "supervisor";

  return (
    <div className="container-fluid">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 gap-2">
        <h2 className="fw-bold text-black mb-0 fs-24">Transactions</h2>
        {!isSupervisor && (
          <div className="d-flex flex-wrap align-items-center gap-2">
            <CommonButton
              variant="primary"
              onClick={() => setShowCreateSingleModal(true)}
            >
              Create Single Transaction
            </CommonButton>
            <CommonButton
              variant="primary"
              onClick={() => setShowCreateBulkModal(true)}
            >
              Create Multiple Transaction
            </CommonButton>
          </div>
        )}
      </div>
      <div className="card mb-4" style={{ borderRadius: "10px" }}>
        <div className="card-body p-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <div style={{ width: "200px" }}>
                <CommonInput
                  type="text"
                  placeholder="Enter ID"
                  value={filters.transactionId}
                  onChange={(e) =>
                    handleFilterChange("transactionId", e.target.value)
                  }
                />
              </div>
              <div style={{ width: "220px" }}>
                <CommonSelect
                  placeholder="Select payment method"
                  options={paymentMethodOptions}
                  value={filters.paymentMethod}
                  onChange={(val) => handleFilterChange("paymentMethod", val)}
                />
              </div>
              <div style={{ width: "200px" }}>
                <CommonSelect
                  placeholder="Select status"
                  options={statusOptions}
                  value={filters.status}
                  onChange={(val) => handleFilterChange("status", val)}
                />
              </div>
              <CommonButton onClick={handleReset} variant="outline">
                <i className="flaticon-381-repeat-1"></i>
              </CommonButton>
              <CommonButton onClick={handleSearch} className="px-3">
                <i className="flaticon-381-search-1 fs-18 text-white"></i>
              </CommonButton>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="card-body">
          <DataTable
            columns={columns}
            data={displayData}
            showPagination={false}
            minWidth="100%"
          />
        </div>
      </div>

      <TransactionInformationModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        transaction={selectedTransaction}
      />

      <CreateSingleTransactionModal
        show={showCreateSingleModal}
        onHide={() => setShowCreateSingleModal(false)}
      />

      <SendBulkTransactionModal
        show={showCreateBulkModal}
        onHide={() => setShowCreateBulkModal(false)}
      />
    </div>
  );
};

export default TransactionsList;
